import React, { useEffect, useRef } from "react";
import { 
  gl_start, mxm, scale, move, turnZ, setUniform, 
  drawObj, Matrix 
} from "./webgl9.js";
import { implicitSurfaceTriangleMesh, Blobs } from "./implicit.js";

export default function WebCanvas() {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  
  // Shared animation state
  const animationStateRef = useRef({
    time: 0,
    bend: 0,
    distance: 0 // Distance between blobs
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function Scene() {
      this.vertexShader = `#version 300 es
uniform mat4 uMF, uMI, uMat[20];
in  vec3 aPos, aNor, aWts0, aWts1;
out vec3 vPos, vNor;
void main() {
    vec4 p = uMF * vec4(aPos, 1.);
    vec4 pos = vec4(0.);
    for (int i = 0 ; i < 20 ; i++) {
      if (i == int(aWts0.x)) pos += mod(aWts0.x, 1.) * (uMat[i] * p);
      if (i == int(aWts0.y)) pos += mod(aWts0.y, 1.) * (uMat[i] * p);
      if (i == int(aWts0.z)) pos += mod(aWts0.z, 1.) * (uMat[i] * p);
      if (i == int(aWts1.x)) pos += mod(aWts1.x, 1.) * (uMat[i] * p);
      if (i == int(aWts1.y)) pos += mod(aWts1.y, 1.) * (uMat[i] * p);
      if (i == int(aWts1.z)) pos += mod(aWts1.z, 1.) * (uMat[i] * p);
    }
    vec4 nor = vec4(aNor, 0.) * uMI;
    gl_Position = pos * vec4(1.,1.,-.1,1.);
    vPos = pos.xyz;
    vNor = nor.xyz;
}`;

      this.fragmentShader = `#version 300 es
precision highp float;
in  vec3 vPos, vNor;
out vec4 fragColor;
uniform vec3 uColor;

void main() {
   vec3 nor = normalize(vNor);
   float c = .1 + max(0., dot(vec3( .5),nor))
                + max(0., dot(vec3(-.5),nor));
   fragColor = vec4(c * uColor, 1.);
}`;

      let blobs = new Blobs();

      let x = 0.15;
      let y = 0.05;

      let m1 = mxm(move(-x, -y, 0), scale(0.15));
      let m2 = mxm(move(-x * 0.1, 0.0, 0), scale(0.15));
      let m3 = mxm(move(x, -y, 0), scale(0.15));

      blobs.addBlob(blobs.SPHERE, m1, .5);
      blobs.addBlob(blobs.SPHERE, m2, .5);
      blobs.addBlob(blobs.SPHERE, m3, .5);

      let mesh = { data: implicitSurfaceTriangleMesh(blobs, 150) };
      let M = new Matrix();

      this.update = () => {
  const time = Date.now() / 1000;
  const bend = Math.cos(time) * 0.5 - 0.5; // bending oscillation

  // --- Parameters ---
  const baseX = -0.2;          // base X offset
  const baseY = 0.2;          // base Y offset
  const bendX = -0.1;          // how much X moves with bend
  const bendY = -0;         // how much Y moves with bend (smaller to reduce vertical shift)
  const step1 = 0.3;           // first horizontal move
  const step2 = 0.05;          // subsequent small steps
  const stepBend = -0.1;       // adjustment per bend for second step

  let A, B, C;
  M.push();

    // initial move with bend
    M.move(baseX + bendX * bend, baseY + bendY * bend, 0);
    M.turnZ(-Math.PI/2 - bend);

    // first segment
    M.move(step1, 0, 0);
    A = M.get();

    // second segment
    M.move(step2, 0, 0);
    M.turnZ(bend);
    M.move(step2 + stepBend * bend, 0, 0);
    B = M.get();

    // third segment
    M.move(step2, 0, 0);
    M.turnZ(bend);
    M.move(step2, 0, 0);
    C = M.get();

  M.pop();


        // Calculate distance between blobs (approximate)
        // When bend is negative (-.5 to 0), the arm is more extended
        // When bend is 0, the arm is compressed
        let distance = Math.abs(bend); // 0 to 0.5, where 0 means compressed

        // Update shared animation state
        animationStateRef.current = {
          time: time,
          bend: bend,
          distance: distance
        };

        setUniform('Matrix4fv', 'uMat', false, [A,B,C].flat());
        
        let gl = canvas.gl;
        let vertexSize = 0;
        let map = ['aPos',3,'aNor',3,'aWts0',3,'aWts1',3];
        for (let n = 0; n < map.length; n += 2)
          vertexSize += map[n+1];
        let index = 0;
        for (let n = 0; n < map.length; n += 2) {
          let attr = gl.getAttribLocation(gl.program, map[n]);
          gl.enableVertexAttribArray(attr);
          gl.vertexAttribPointer(attr, map[n+1], gl.FLOAT, false, vertexSize * 4, index * 4);
          index += map[n+1];
        }
        
        // let color = [0.5, 0.5, 0.5];
        let color = [0.345, 0.831, 0.424];
        
        drawObj(mesh, scale(2), color, 12);
      }
    }

    gl_start(canvas, new Scene());
    
    return () => canvas.stopRender && canvas.stopRender();
  }, []);

  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;

    let offset = 0;
    const WAVE_SPEED = 1.0; // Speed when bend is active

    function drawWave() {
      ctx.clearRect(0, 0, w, h);

      // Get current animation state
      const { bend } = animationStateRef.current;

      const THRESHOLD = -0.005;

      // If bend is below threshold (more compressed), move the wave
      if (bend < THRESHOLD) {
        // The more compressed (more negative), the faster it moves
        // bend ranges from -0.5 to 0
        // When bend = -0.5, movement is maximum
        // When bend = -0.2, movement just starts
        const intensity = Math.abs(Math.min(0, bend + 0.1)) / 2.0; // smaller range → fades faster

        offset += WAVE_SPEED * intensity;
      }

      const waveAmplitude = 6; // Constant amplitude

      ctx.fillStyle = "rgba(72, 32, 8, 1)";
      ctx.beginPath();
      ctx.moveTo(0, h);

      for (let x = 0; x <= w; x += 10) {
        let y = h - 120 + Math.sin((x + offset) * 0.05) * waveAmplitude;
        ctx.lineTo(x, y);
      }

      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.fill();

      requestAnimationFrame(drawWave);
    }

    drawWave();
  }, []);

  return (
    <div style={{ 
      position: "relative", 
      display: "inline-block", 
      width: "400px",
      height: "400px",
    }}>
      <canvas 
        ref={canvasRef} 
        width={400} 
        height={400}
        style={{ 
          width: "400px", 
          height: "400px", 
          display: "block", 
          // background: "black",
          transform: "rotate(-90deg)",
          transformOrigin: "center center",
          position: "absolute",
          left: 0,
          top: 0,
        }} 
      />
      <canvas 
        ref={overlayRef} 
        width={400} 
        height={400}
        style={{ 
          width: "400px", 
          height: "400px", 
          display: "block",
          position: "absolute",
          left: 0,
          top: 0,
          zIndex : -1,
          pointerEvents: "none",
          background : "black"
        }} 
      />

       {/* <div style={{
        position: "absolute",
        bottom: "10px",
        left: "10px",
        color: "white",
        fontSize: "12px",
        background: "rgba(0,0,0,0.5)",
        padding: "5px"
      }}>
        Bend: {animationStateRef.current.bend.toFixed(2)}<br/>
        Distance: {animationStateRef.current.distance.toFixed(2)}
      </div> */}

    </div>
  );
}